import { useCallback, useEffect, useRef, useState } from "react";
import { NinjaArt } from "../art/NinjaArt";
import { Burst } from "../components/Burst";
import { Combo } from "../components/Combo";
import { HealthBar } from "../components/HealthBar";
import { HintView } from "../components/HintView";
import { Keypad } from "../components/Keypad";
import { Modal } from "../components/Modal";
import { QuestionView } from "../components/QuestionView";
import { BATTLE_RULES } from "../content/battles";
import type { Mode } from "../content/modes";
import type { Ninja } from "../content/ninjas";
import { RANKS } from "../content/ranks";
import {
  battleResult,
  battleSource,
  boostLeft,
  canUseItem,
  slowLeft,
  type BattleContext,
  type BattleResult,
  type BattleState,
  type Blow,
} from "../game/battle";
import { itemById } from "../content/items";
import { COMBO_TIERS } from "../content/combo";
import { comboOf, comboTier, isTierStep } from "../game/combo";
import { ItemArt } from "../art/ItemArt";
import { hintFor } from "../game/hints";
import { kitNames, type Kit } from "../game/items";
import { solvedText, spokenText } from "../game/questions/question";
import { seededRng } from "../game/rng";
import { canDiscard, formatClock, LOW_TIME_SECONDS } from "../game/round";
import { useBattle } from "../hooks/useBattle";
import { useKeyboard } from "../hooks/useKeyboard";
import type { SaveState } from "../save/schema";
import { speak, stopSpeaking } from "../speech/speech";
import "./Play.css";
import "./Battle.css";

interface Props {
  save: SaveState;
  mode: Mode;
  enemy: Ninja;
  fighter: Ninja;
  /** Whether the child has met this ninja; otherwise it fights as a silhouette. */
  known: boolean;
  seed: number;
  /** What the fighter carries (game/items.ts fitFor). */
  kit: Kit;
  audio: boolean;
  onFinished: (result: BattleResult & { durationMs: number | null }) => void;
  onStopped: () => void;
}

/** What the screen shouts for each blow. */
export function blowText(b: Blow, fighter: Ninja): string {
  switch (b.kind) {
    case "hit": {
      const hit = b.times > 1 ? `Hit for ${b.damage}! ×${b.times}` : `Hit for ${b.damage}!`;
      return b.focus ? `${hit} Focus +${b.focus}s` : hit;
    }
    case "hurt":
      return `Ouch! −${b.damage}`;
    case "dodge":
      return "Dodged!";
    case "shield":
      return "Blocked!";
    case "move":
      return `${fighter.move.name}!`;
    case "item":
      return `${itemById(b.id)?.name ?? "Item"}!`;
  }
}

/** What's in effect right now: "×2 for 7s", "×3 next 2 hits", "Shield 2". */
export function effectText(s: BattleState, now: number): string | null {
  const left = boostLeft(s, now);
  if (s.boost && left > 0) return `×${s.boost.times} for ${left}s`;
  const slow = slowLeft(s, now);
  if (slow > 0) return `Slow Time ${slow}s`;
  if (s.charge)
    return `×${s.charge.times} next ${s.charge.hits} hit${s.charge.hits === 1 ? "" : "s"}`;
  if (s.shield > 0) return `Shield ${s.shield}`;
  return null;
}

export function Battle({
  save,
  mode,
  enemy,
  fighter,
  known,
  seed,
  kit,
  audio,
  onFinished,
  onStopped,
}: Props) {
  // Questions and dodges each get their own stream from the seed, so a battle replays.
  const [ctx] = useState<BattleContext>(() => ({
    fighter,
    enemy,
    kit,
    next: battleSource({
      mode,
      rank: save.rank[mode],
      ops: save.settings.ops,
      practice: save.settings.practice,
      mastery: save.mastery,
      missed: save.missed,
      rng: seededRng(seed),
    }),
    rng: seededRng((seed ^ 0x5bd1e995) >>> 0),
  }));
  const { state, seconds, now, press, stop, finish, pass, move, hint, pressItem } = useBattle(
    ctx,
    true,
  );
  const round = state.round;
  const live = round.phase === "live" || round.phase === "feedback";
  const [confirming, setConfirming] = useState(false);
  const keepGoing = useRef<HTMLButtonElement>(null);

  useKeyboard(press, live && !confirming, pass);

  const onStop = useCallback(() => {
    if (canDiscard(round, Date.now())) stop();
    else setConfirming(true);
  }, [round, stop]);
  const closeConfirm = useCallback(() => setConfirming(false), []);

  const startedAt = useRef<number | null>(null);
  useEffect(() => {
    if (live && startedAt.current === null) startedAt.current = Date.now();
  }, [live]);

  // Hand the finished or abandoned battle to the app once, after render.
  useEffect(() => {
    const result = battleResult(state, kit);
    if (result)
      onFinished({
        ...result,
        durationMs: startedAt.current === null ? null : Date.now() - startedAt.current,
      });
    else if (round.phase === "abandoned") onStopped();
  }, [state, kit, round.phase, onFinished, onStopped]);

  const question = live ? round.question : null;
  useEffect(() => {
    if (audio && question) speak(spokenText(question.tokens));
  }, [audio, question]);
  useEffect(() => stopSpeaking, []);
  const hear = useCallback(() => {
    if (question) speak(spokenText(question.tokens));
  }, [question]);

  if (round.phase === "countdown") {
    if (round.count === 0) return <section className="screen play battle" />;
    return (
      <section className="screen play play-countdown" aria-live="assertive">
        <div className="count display" key={round.count}>
          {round.count}
        </div>
        <p className="count-tip">
          {known ? enemy.name : "A mystery ninja"} has {enemy.health} health. Every right answer
          hits!
        </p>
      </section>
    );
  }
  if (!live) return null;

  const fb = round.feedback;
  const low = seconds !== null && seconds <= LOW_TIME_SECONDS;
  const fighterRank = RANKS[fighter.rankIndex]!;
  const enemyRank = RANKS[enemy.rankIndex]!;
  const blow = state.blow;
  const youAnim = blow?.kind === "hit" ? " lunge" : blow?.kind === "hurt" ? " struck" : "";
  const foeAnim = blow?.kind === "hit" ? " struck" : blow?.kind === "hurt" ? " lunge" : "";
  const calloutSide = blow?.kind === "hit" ? "foe" : "you";
  // The art and the callout are re-keyed on each blow so their animations restart.
  // Their keys must differ: siblings sharing a key made React draw the ninja twice.
  const effect = effectText(state, now);
  const hintModel = question ? hintFor(question) : null;
  const canMove = !state.moveUsed && round.phase === "live" && !confirming;
  const slow = slowLeft(state, now);
  const worn = kit.passive.length ? kitNames({ active: [], passive: kit.passive }) : null;
  // Combos are read off the log, so they outlive the feedback flash. A higher
  // tier lunges further; it never hits harder.
  const combo = comboOf(round.log);
  const tier = comboTier(combo);
  const burst = isTierStep(combo) && tier && (tier.sparkles > 0 || tier.flash) ? tier : null;
  const reach = 22 + 8 * (tier ? COMBO_TIERS.indexOf(tier) + 1 : 0);

  return (
    <section className="screen play battle">
      <div className="hud">
        <div
          className={"timer display" + (low ? " low" : "") + (slow > 0 ? " slow" : "")}
          aria-live="off"
        >
          {formatClock(seconds ?? 0)}
          {slow > 0 && <span className="slow-tag">slow {slow}s</span>}
        </div>
        <div className="hud-mid">
          <div key={state.points} className={"score" + (state.points ? " bump" : "")}>
            {state.points} points
          </div>
          <Combo count={combo} rankColour={fighterRank.colour} />
        </div>
        <button type="button" className="ghost" onClick={onStop}>
          Stop
        </button>
      </div>
      <div className="arena battle-arena">
        <div className="fight" style={{ "--reach": `${reach}px` } as React.CSSProperties}>
          <div className="fighter you" style={{ "--c": fighterRank.colour } as React.CSSProperties}>
            <div key={`art-${blow?.n ?? 0}`} className={"fart" + youAnim}>
              <NinjaArt rank={fighterRank} className="fighter-art" />
            </div>
            <div className="fname display">{fighter.name}</div>
            <HealthBar
              value={state.health}
              max={BATTLE_RULES.playerHealth}
              label={fighter.name}
              className="fighter-bar"
            />
            <button
              type="button"
              className={"move-btn display" + (state.moveUsed ? " used" : "")}
              onClick={move}
              disabled={!canMove}
              aria-label={`${fighter.move.name}: ${fighter.move.text}${state.moveUsed ? ". Used." : ""}`}
            >
              {fighter.move.name}
            </button>
            <small className="move-text">
              {effect ?? (state.moveUsed ? "Used" : fighter.move.text)}
            </small>
            {kit.active.length > 0 && (
              <div className="item-btns" role="group" aria-label="Items">
                {kit.active.map(({ item, at }) => {
                  const used = state.itemsUsed.includes(item.id);
                  return (
                    <button
                      key={item.id}
                      type="button"
                      className={"item-btn" + (used ? " used" : "")}
                      disabled={!canUseItem(state, kit, item.id) || confirming}
                      aria-label={`${item.name}: ${at.text}${used ? ". Used." : ""}`}
                      onClick={() => pressItem(item.id)}
                    >
                      <ItemArt id={item.id} dim={used} className="item-btn-art" />
                      <span className="item-btn-name">{item.name}</span>
                    </button>
                  );
                })}
              </div>
            )}
            {worn && <small className="worn-text">Wearing {worn}</small>}
            {blow && calloutSide === "you" && (
              <div key={`callout-${blow.n}`} className="callout pop" aria-live="off">
                {blowText(blow, fighter)}
              </div>
            )}
          </div>
          <div className="vs display" aria-hidden="true">
            vs
          </div>
          <div className="fighter foe" style={{ "--c": enemyRank.colour } as React.CSSProperties}>
            <div key={`art-${blow?.n ?? 0}`} className={"fart" + foeAnim}>
              <NinjaArt rank={enemyRank} locked={!known} className="fighter-art" />
            </div>
            <div className="fname display">{known ? enemy.name : "???"}</div>
            <HealthBar
              value={state.enemyHealth}
              max={enemy.health}
              label={known ? enemy.name : "Mystery ninja"}
              className="fighter-bar"
            />
            {blow && calloutSide === "foe" && (
              <div key={`callout-${blow.n}`} className="callout pop" aria-live="off">
                {blowText(blow, fighter)}
              </div>
            )}
          </div>
        </div>
        <div className="question-block">
          <div className="qwrap">
            <QuestionView
              question={round.question}
              input={round.input}
              feedback={fb}
              onHear={audio ? hear : undefined}
            />
            {burst && (
              <Burst
                key={`burst-${round.log.length}`}
                tier={burst}
                rankColour={fighterRank.colour}
              />
            )}
          </div>
          <div className="fix" aria-live="polite">
            {fb?.kind === "wrong" || fb?.kind === "passed" ? solvedText(fb.question) : ""}
          </div>
          <button
            type="button"
            className="ghost pass-btn"
            onClick={pass}
            disabled={round.phase !== "live" || confirming}
          >
            Pass
          </button>
          {state.hint && hintModel ? (
            <HintView hint={hintModel} />
          ) : (
            hintModel &&
            state.hintsLeft > 0 && (
              <button
                type="button"
                className="mini show-me"
                onClick={hint}
                disabled={round.phase !== "live" || confirming}
              >
                Show me ({state.hintsLeft} left)
              </button>
            )
          )}
        </div>
        <Keypad onPress={press} disabled={round.phase !== "live" || confirming} />
      </div>
      <Modal
        open={confirming}
        label="Retreat from the battle?"
        onClose={closeConfirm}
        initialFocus={keepGoing}
      >
        <div className="panel welcome">
          <h2 className="display">Retreat?</h2>
          <p>You keep the coins you&apos;ve earned. The clock keeps going while you decide.</p>
          <button ref={keepGoing} type="button" className="big display" onClick={closeConfirm}>
            Keep fighting
          </button>
          <button
            type="button"
            className="ghost"
            onClick={() => {
              setConfirming(false);
              finish();
            }}
          >
            Retreat
          </button>
        </div>
      </Modal>
    </section>
  );
}
